import type { FormStore } from "shared/react";
import type { ChallengeData, ChallengeFormData, RewardType, TaskData } from "shared/schema";
import type { AsyncValueCallback, ImmutableDictionary } from "shelving";
import { assertDefined, omitProps, withProp } from "shelving";
import { useStore } from "shelving/react";
import { Button, CheckboxField, DateField, Field, Form, FormCondition, Icon, Loader, NumberField, RadiosField, SelectField, SubmitButton, TextAreaField, TextField, closeEventDialog, useDialog, useForm, useFormContext } from "shared/react";
import { CHALLENGE_DETAILS_SCHEMA, CHALLENGE_FORM_SCHEMA, EMPTY_CHALLENGE, EMPTY_TASK, TASK_SCHEMA } from "shared/schema";
import { RawChallengeCard } from "../challenge/ChallengeCard.js";
import { TypeUnitField } from "../misc/UnitField.js";
import { TrajectoryCreatedRewardPicker } from "../reward/RewardPicker.js";
import { CreatorPicker } from "./CreatorPicker.js";
import { VerifiedCondition } from "./VerifiedCondition.js";

type ChallengeFormStore = FormStore<ChallengeFormData>;

export function ChallengeForm({
	data, //
	onSubmit,
	children,
}: {
	onSubmit?: AsyncValueCallback<ChallengeFormData>;
	children: React.ReactNode;
	data?: Partial<ChallengeFormData>;
}): React.ReactElement {
	const showDialog = useDialog();

	const form = useStore(useForm(CHALLENGE_FORM_SCHEMA, { ...EMPTY_CHALLENGE, ...data }));
	const creator = form.value?.creator;

	const editDetails = () => showDialog(<ChallengeDetailsModal form={form} />);
	const pickBadge = () => showDialog(<ChallengeRewardModal type="badge" form={form} />);
	const pickBackground = () => showDialog(<ChallengeRewardModal type="background" form={form} />);
	const pickReaction = () => showDialog(<ChallengeRewardModal type="reaction" form={form} />);
	const pickCreator = () => showDialog(<ChallengeCreatorModal form={form} />);
	const addTask = () => showDialog(<ChallengeTaskModal form={form} />);

	return (
		<form.Form onSubmit={onSubmit}>
			<div
				onClick={e => {
					e.preventDefault();
					e.stopPropagation();
					if (e.target instanceof Element) {
						const task = e.target.closest("[data-task]");
						if (task instanceof HTMLElement) showDialog(<ChallengeTaskModal form={form} id={task.dataset["task"]} />);
						else if (e.target.closest("footer .button")) pickCreator();
						else if (e.target.closest("header .icon")) pickBadge();
						else if (e.target.closest("header :has(> h3)")) editDetails();
						else pickBackground();
					}
					return false; // Needed for iOS.
				}}
			>
				<ChallengeFormCard />
			</div>
			<section className="flex-row flex-wrap size-small gap-hairline space-hairline">
				<Button onClick={editDetails}>
					<Icon icon="challenge" /> <span>Details</span>
				</Button>
				<Button onClick={pickBadge}>
					<Icon icon="badge" /> <span>Badge</span>
				</Button>
				<Button onClick={pickBackground}>
					<Icon icon="background" /> <span>Background</span>
				</Button>
				<VerifiedCondition entity={creator}>
					<Button onClick={pickReaction}>
						<Icon icon="reaction" /> <span>Reaction</span>
					</Button>
				</VerifiedCondition>
				<Button onClick={addTask}>
					<Icon icon="add" /> <span>Add task</span>
				</Button>
			</section>
			{children}
		</form.Form>
	);
}

/**
 * Re-key an object to use keys `a` to `z` string keys.
 *
 * Because: Firestore stores map objects alphabetically, so maps with randomly generated IDs would not not retrieved in the same order they're stored in.
 * This method rekeys the object using alphabetically indexed keys to preserve initial ordering.
 */
function _rekeyObject(obj: ImmutableDictionary<TaskData>): ImmutableDictionary<TaskData> {
	return Object.fromEntries(Object.values(obj).map((v, i) => [String.fromCharCode(i + 97), v]));
}

/** Show a `<ChallengeCard>` component using the current value of a `ChallengeForm` instance. */
function ChallengeFormCard(): React.ReactElement {
	const form = useStore(useFormContext<ChallengeFormData>());
	return <RawChallengeCard challenge={form.data as ChallengeData} attempt={undefined} />;
}

function ChallengeCreatorModal({ form }: { form: ChallengeFormStore }): React.ReactElement {
	return (
		<article className="modal">
			<Loader>
				<CreatorPicker onPick={x => form.setProp("creator", x)} creator={form.data.creator} />
			</Loader>
		</article>
	);
}

function ChallengeDetailsModal({ form }: { form: ChallengeFormStore }): React.ReactElement {
	return (
		<article className="modal">
			<h2>Details</h2>
			<Form //
				schema={CHALLENGE_DETAILS_SCHEMA}
				onSubmit={v => form.update(v)}
				data={form.data}
			>
				<Field title="Details">
					<TextField name="name" />
					<TextAreaField name="about" rows={2} />
				</Field>
				<Field title="Dates" description="Challenge must be started between these two dates.">
					<div className="flex-row flex-stretch gap-hairline">
						<DateField name="start" />
						<DateField name="end" />
					</div>
				</Field>
				<RadiosField name="privacy" />
				<SubmitButton>Save challenge</SubmitButton>
			</Form>
		</article>
	);
}

function ChallengeTaskModal({ id, form }: { id?: string | undefined; form: ChallengeFormStore }): React.ReactElement {
	const tasks = form.data.tasks;
	return (
		<article className="modal">
			<h2>{id ? "Edit task" : "Add task"}</h2>
			<Form<TaskData> //
				schema={TASK_SCHEMA}
				onSubmit={data => {
					form.setProp("tasks", _rekeyObject(withProp(form.data.tasks || {}, id || "z", data)));
				}}
				data={(id && tasks?.[id]) || { type: EMPTY_TASK.type }}
			>
				<Field>
					<TextField name="title" mods="space-hairline" />
					<div className="flex-row gap-hairline space-hairline">
						<SelectField name="type" mods="flex-half" />
						<FormCondition<TaskData> onChange={({ type = EMPTY_TASK.type }) => type !== "checkbox"}>
							<NumberField name="soloTarget" title="Target" mods="flex-quarter text-right" />
						</FormCondition>
						<TypeUnitField name="units" mods="flex-quarter" />
					</div>
				</Field>
				<Field>
					<div className="flex-row gap-hairline">
						<CheckboxField name="shared" mods="flex-half" />
						<FormCondition<TaskData> onChange={({ shared = EMPTY_TASK.shared }) => shared}>
							<NumberField name="sharedTarget" title="Goal" mods="flex-quarter text-right" />
							<TypeUnitField name="units" mods="flex-quarter" />
						</FormCondition>
					</div>
					<FormCondition<TaskData> onChange={({ shared = EMPTY_TASK.shared }) => shared}>
						<p className="color-quiet size-small space-xxsmall">User progress is contributed to a single shared goal. All users who take part in the challenge will be able to claim the rewards.</p>
					</FormCondition>
				</Field>
				<SubmitButton>{id ? "Save task" : "Add task"}</SubmitButton>
				{id && tasks && Object.keys(tasks).length > 1 ? (
					<Button
						mods="button-quiet confirm"
						onClick={e => {
							closeEventDialog(e);
							form.setProp("tasks", _rekeyObject(omitProps(form.validated.tasks, id)));
						}}
					>
						Delete task
					</Button>
				) : null}
			</Form>
		</article>
	);
}

function ChallengeRewardModal({ type, form }: { type: RewardType; form: ChallengeFormStore }) {
	const { creator } = form.data;
	assertDefined(creator);
	return (
		<article className="modal">
			<Loader>
				<TrajectoryCreatedRewardPicker //
					type={type}
					onPick={id => form.setProp(type, id)}
					creator={creator}
				/>
			</Loader>
		</article>
	);
}
